/**
 * profesoresStore.js — profesores (trabajadores) de la autoescuela del simulador y su horario semanal,
 * en memoria. Espeja lo que en la app real guardan `/usuarios` (alta, nombre, borrado) y
 * `/peluqueria/user-schedule` (horario + periodos especiales).
 *
 * La lista vive en `SECTOR_DATA_MAP.autoescuela.mockInstructors` y se muta EN SITIO (como hacen el
 * asistente y sedesStore), así que Planificación, Flota, Alumnos, Dashboard… ven el mismo profesor
 * sin tocar cada pantalla. El horario lo leen el editor de horario (vía el axiosClient del simulador)
 * y el calendario de Planificación (franjas fuera de horario de cada profesor).
 *
 * `resetProfesoresDemo()` vuelve a lo de fábrica: lo llama «Salir del simulador» (resetSimDemoSession).
 */
import { useSyncExternalStore } from "react";
import { SECTOR_DATA_MAP } from "../../mockData";
import { horasAbiertasProfesor } from "./horarioProfesor";
import { normalizaPermisos, validaPermisos } from "./permisosUsuario";

export const SCHEDULE_DAYS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"]; // índice = getDay()

const range = (start, end) => [{ start, end }];
function weekSchedule(days, start, end) {
  const out = { lunes: [], martes: [], "miércoles": [], jueves: [], viernes: [], "sábado": [], domingo: [] };
  days.forEach((d) => { out[d] = range(start, end); });
  return out;
}
const LV = ["lunes", "martes", "miércoles", "jueves", "viernes"];

// Horario de fábrica de los tres profesores de ejemplo (antes, un texto fijo en el modal). Cubre TODAS las
// prácticas de ejemplo (de 08:30 a 19:30, y el sábado por la mañana las de Miguel Ángel): si no, la demo
// enseñaría prácticas encima de la zona rayada de «fuera de horario» en Planificación.
const FACTORY_SCHEDULES = {
  "inst-001": weekSchedule(LV, "08:00", "20:00"),
  "inst-002": weekSchedule(LV, "09:00", "20:00"),
  "inst-003": { ...weekSchedule(LV, "08:00", "20:00"), "sábado": range("09:00", "14:00") },
};

const lista = () => {
  const data = SECTOR_DATA_MAP.autoescuela;
  if (!data) return [];
  if (!Array.isArray(data.mockInstructors)) data.mockInstructors = [];
  return data.mockInstructors;
};
const clone = (v) => JSON.parse(JSON.stringify(v));

// Foto de fábrica: los MISMOS objetos (sedesStore les pone `sede_ids` en sitio) y sus campos editables.
const FACTORY_ORDER = lista().map((i) => i.instructor_id);
const FACTORY_OBJECTS = new Map(lista().map((i) => [i.instructor_id, i]));
const FACTORY_FIELDS = new Map(lista().map((i) => [i.instructor_id, { name: i.name, phone: i.phone || "", horario: i.horario || "" }]));

let _schedules = clone(FACTORY_SCHEDULES);
let _overrides = {};
let _askProfesor = true; // «Preguntar al alumno con qué profesor quiere reservar»
// Permisos por usuario (8/10/2026): qué puede hacer cada profesor (espejo de c_usuarios.autoescuela_permisos).
// De fábrica nada: su planificación sin tocarla y su fichaje. Ficha: docs/features/autoescuela-permisos-usuario.md
let _permisos = {};
let _version = 0;
const _subs = new Set();

function emit() {
  _version += 1;
  _subs.forEach((fn) => { try { fn(); } catch { /* un suscriptor roto no tumba a los demás */ } });
}

export function subscribeProfesores(fn) {
  _subs.add(fn);
  return () => _subs.delete(fn);
}
const getVersion = () => _version;
/** Número que cambia en cada alta/edición/borrado/horario: para volver a pintar quien lee la lista. */
export function useProfesoresVersion() {
  return useSyncExternalStore(subscribeProfesores, getVersion, getVersion);
}

export function getProfesoresList() {
  return lista();
}
export function getProfesor(id) {
  return lista().find((i) => String(i.instructor_id) === String(id)) || null;
}
export function isProfesorId(id) {
  return !!getProfesor(id);
}

// Mismo id que la real (`new_user_id` = nombre en minúsculas con guiones), sin pisar uno que ya exista.
function idFromName(name) {
  const base = String(name).toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "") || "profesor";
  let id = base;
  let n = 2;
  while (getProfesor(id)) { id = `${base}-${n}`; n += 1; }
  return id;
}

/** Alta de un profesor (sin horario todavía: la real encadena con el editor de horario). */
export function addProfesor(name) {
  const clean = String(name || "").trim();
  if (!clean) return null;
  const inst = { instructor_id: idFromName(clean), name: clean, phone: "", horario: "", sim_demo_created: true };
  lista().push(inst);
  _schedules[inst.instructor_id] = {};
  emit();
  return inst;
}

export function updateProfesor(id, { name, phone } = {}) {
  const p = getProfesor(id);
  if (!p) return null;
  if (typeof name === "string" && name.trim()) p.name = name.trim();
  if (typeof phone === "string") p.phone = phone;
  emit();
  return p;
}

export function deleteProfesor(id) {
  const l = lista();
  const idx = l.findIndex((i) => String(i.instructor_id) === String(id));
  if (idx < 0) return false;
  l.splice(idx, 1);
  delete _schedules[id];
  delete _overrides[id];
  emit();
  return true;
}

/** Lo que devuelve GET /peluqueria/user-schedule para ese profesor. */
export function getProfesorSchedule(id) {
  return { schedule: clone(_schedules[id] || {}), schedule_overrides: clone(_overrides[id] || []) };
}

/** PUT /peluqueria/user-schedule. */
export function setProfesorSchedule(id, schedule, scheduleOverrides) {
  _schedules[id] = clone(schedule || {});
  if (Array.isArray(scheduleOverrides)) _overrides[id] = clone(scheduleOverrides);
  emit();
}

export function getAskProfesor() {
  return _askProfesor;
}
export function setAskProfesor(v) {
  _askProfesor = !!v;
  emit();
}

/**
 * Franjas en las que el profesor trabaja ese día, en minutos desde medianoche ([{start,end}]), con sus
 * periodos especiales por encima del horario habitual. `null` si no tiene horario configurado: entonces
 * no se marca nada (un profesor recién creado no sale con la columna entera en gris). La lógica es la de
 * la app real (horarioProfesor.js, copia por valor).
 */
export function openRangesForProfesor(id, date) {
  return horasAbiertasProfesor(_schedules[id], _overrides[id], date);
}

/** Permisos de un profesor ({ id: bool } con todos los ids). */
export function getPermisosProfesor(id) {
  return normalizaPermisos(_permisos[id]);
}
/** Guarda los permisos (valida como c_updateUser). → { ok } | { error } */
export function setPermisosProfesor(id, permisos) {
  const v = validaPermisos(permisos);
  if (v.error) return { error: v.error };
  _permisos = { ..._permisos, [id]: v.value };
  emit();
  return { ok: true };
}

/** Lo que devuelve GET /peluqueria/workers-schedules en la app real: { schedules, overrides } por profesor. */
export function getHorariosProfesores() {
  return { schedules: clone(_schedules), overrides: clone(_overrides) };
}

/**
 * ¿Es un profesor de ejemplo que se ha quitado con el «−» de Planificación? Sus prácticas de ejemplo dejan de
 * verse (si no, saldrían en «Sin asignar» y la demo de 2 profesores enseñaría 3).
 */
export function profesorDemoQuitado(id) {
  return !!id && FACTORY_OBJECTS.has(String(id)) && !getProfesor(id);
}

const MAX_PROFESORES_DEMO = 15;
/**
 * «Profesores: − 3 +» de Planificación (30/10/2026, David: «si me dicen que son 2 profes, darle al − y que se
 * quede en 2»). Quitar = el último de la lista (con sus prácticas de ejemplo ocultas). Poner = primero los de
 * ejemplo quitados (con su horario de fábrica) y luego «Profesor 4», «Profesor 5»… con horario L-V 8:00-20:00.
 * Devuelve el número final.
 */
export function setNumeroProfesoresDemo(n) {
  const objetivo = Math.max(1, Math.min(MAX_PROFESORES_DEMO, Math.round(Number(n) || 1)));
  const l = lista();
  while (l.length > objetivo) {
    const ultimo = l.pop();
    delete _schedules[ultimo.instructor_id];
    delete _overrides[ultimo.instructor_id];
  }
  while (l.length < objetivo) {
    const vuelve = FACTORY_ORDER.find((id) => !getProfesor(id));
    if (vuelve) {
      const obj = FACTORY_OBJECTS.get(vuelve);
      Object.assign(obj, FACTORY_FIELDS.get(vuelve));
      l.push(obj);
      _schedules[vuelve] = clone(FACTORY_SCHEDULES[vuelve] || {});
      continue;
    }
    let k = l.length + 1;
    while (getProfesor(`profesor-${k}`)) k += 1;
    const inst = { instructor_id: `profesor-${k}`, name: `Profesor ${k}`, phone: "", horario: "L-V 8:00-20:00", sim_demo_created: true };
    l.push(inst);
    _schedules[inst.instructor_id] = weekSchedule(LV, "08:00", "20:00");
  }
  emit();
  return l.length;
}

/** Vuelve a lo de fábrica: los tres profesores de ejemplo con su nombre y horario, sin los creados. */
export function resetProfesoresDemo() {
  const l = lista();
  const fabrica = FACTORY_ORDER.map((id) => {
    const obj = FACTORY_OBJECTS.get(id);
    Object.assign(obj, FACTORY_FIELDS.get(id));
    return obj;
  });
  l.splice(0, l.length, ...fabrica);
  _schedules = clone(FACTORY_SCHEDULES);
  _overrides = {};
  _askProfesor = true;
  _permisos = {};
  emit();
}
